import React, { useState, useEffect } from 'react';
import { adminService } from '../services/api';

function formatUSD(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return '$' + (n / 1000).toFixed(0) + 'K';
  return '$' + Number(n).toLocaleString();
}

function BarChart({ data, title }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="chart-panel">
      <h4>{title}</h4>
      <div className="bar-list">
        {data.map(({ label, count }) => (
          <div key={label} className="bar-row">
            <span className="bar-label">{label || '(none)'}</span>
            <div className="bar-track">
              <div className="bar-fill" style={{ width: `${(count / max) * 100}%` }} />
            </div>
            <span className="bar-count">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function KPI({ label, value, sub, color }) {
  return (
    <div className={`kpi-card${color ? ' kpi-' + color : ''}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

export default function CKDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    adminService.getAnalytics()
      .then(setData)
      .catch(() => setError('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Loading dashboard…</div>;
  if (error) return <div className="message error">{error}</div>;
  if (!data) return null;

  const { summary, byStage, byDealType, byDealValueType, byResellSubCat, byPsSubCat, byPartner, byCountry, arrByDealType } = data;

  return (
    <div className="ck-dashboard">
      <div className="dashboard-header">
        <h2>CPN Dashboard</h2>
        <span className="dashboard-subtitle">All Partner Opportunities</span>
      </div>

      {/* KPIs */}
      <div className="kpi-row">
        <KPI label="Pipeline ARR" value={formatUSD(summary.total_arr_usd)} color="blue" />
        <KPI label="Submitted" value={summary.submitted} color="amber" sub="Awaiting review" />
        <KPI label="In Review" value={summary.in_review} sub="Being assessed" />
        <KPI label="Approved" value={summary.approved} color="green" sub="CK engaged" />
        <KPI label="Active" value={summary.active} color="green" sub="In progress" />
        <KPI label="Launched" value={summary.closed_won ?? 0} sub="Converted" />
        <KPI label="Partners" value={summary.partners ?? 0} sub="Unique companies" />
      </div>

      {/* ARR by Deal Type */}
      <div className="dashboard-section">
        <h3>ARR by Deal Type</h3>
        <div className="arr-type-row">
          {Object.entries(arrByDealType || {}).map(([type, arr]) => (
            <div key={type} className="arr-type-card">
              <div className="arr-type-label">{type || '(unknown)'}</div>
              <div className="arr-type-value">{formatUSD(arr)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts grid */}
      <div className="charts-grid">
        <BarChart data={byDealType} title="By Deal Type" />
        <BarChart data={byStage} title="By Deal Stage" />
        <BarChart data={byDealValueType} title="By Deal Source" />
        {byResellSubCat && byResellSubCat.length > 0 && (
          <BarChart data={byResellSubCat} title="Resell Sub-Category" />
        )}
        {byPsSubCat && byPsSubCat.length > 0 && (
          <BarChart data={byPsSubCat} title="PS Sub-Category" />
        )}
        <BarChart data={byPartner} title="By Partner" />
        <BarChart data={byCountry} title="By Country" />
      </div>
    </div>
  );
}
